/**
 * @deprecated DO NOT USE - Use FAQ component from @/components/outfame/sections/FAQ instead
 *
 * This component is deprecated and will be removed in a future release.
 * The new FAQ component has:
 * - Modern styling matching Outfame design system
 * - Simpler props interface (items, eyebrow, title)
 * - No HTML support in answers (plain text only)
 *
 * Migration guide:
 * - Import: FAQ, { FAQItem } from "@/components/outfame/sections/FAQ"
 * - Props: faqs → items, title → eyebrow, h3 → title, subtitle → REMOVE
 * - Strip HTML from answers
 *
 * @see src/components/outfame/sections/FAQ.tsx for the new component
 */
"use client"
import { classNames } from "@/lib/utils"
import { Disclosure } from "@headlessui/react"
import { MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/outline"

/**
 * @deprecated Use FAQItem from @/components/outfame/sections/FAQ instead
 */
type Faq = {
    question: string
    answer: string
    hidden?: boolean
}

const defaultFaqs: Faq[] = [
    {
        question: "What is Outfame?",
        answer: "Outfame is a social media growth platform that helps you increase your audience and engagement on Instagram by attracting real users who are interested in your account. <br><br>Outfame grows your account organically through AI targeting of the audience you choose, paid promotion and activity from real people."
    },
    {
        question: "Can I cancel my subscription at any time?",
        answer: "Yes! You can cancel your subscription at any time – free of charge. Simply go to your billing settings and click the cancel button. Your subscription will be cancelled immediately, and you will not be charged again."
    },
    {
        question: "How does Outfame work?",
        answer: "Getting started with Outfame is quick and easy. Simply connect your Instagram account, customize your audience, and adjust your growth settings based on niche, location, gender, age, and other factors. <br><br>Our platform uses advanced AI algorithms to recognize and label each account on multiple data points, so you can attract organic followers who are interested in your specific niche. You can read more about <a href='https://support.outfame.com/en/articles/13584978-how-outfame-works' target='_blank' rel='noopener noreferrer' class='underline'>how Outfame works here</a>. 🙌"
    },
    {
        question: "Is Outfame safe?",
        answer: "Outfame is fully compliant with Instagram's terms of service and does not engage in any spam activity or follow-for-follow practices. We attract real Instagram users who are interested in your account and convert them to followers, without putting your account at risk of getting banned or flagged. Your data is always encrypted and securely stored in our state-of-the-art security architecture."
    },
    {
        question: "How many followers will I gain with Outfame?",
        answer: "The number of followers you gain with Outfame depends on several factors, such as your niche, content quality, and engagement level. On average, Outfame members gain <b>over 4,000 real targeted followers each month</b> on the Grow plan. You can read more about <a href='https://support.outfame.com/en/articles/13585005-are-the-followers-real' target='_blank' rel='noopener noreferrer' class='underline'>how many followers you can gain with Outfame here</a>. 📈"
    },
    {
        question: "What's the difference between Go and Grow?",
        answer: 'Both plans offer real, organic Instagram growth. However, the Grow plan comes with custom growth hacks through <span class="outfame-max-gradient">Outfame Max™</span>, faster growth, and priority 24/7 support.'
    },
    {
        question: "Will Outfame work for my industry?",
        answer: "Yes, Outfame works for any industry! Our AI algorithms recognize and label each account on multiple data points, so you can attract organic followers from your specific niche. Thousands of leading influencers, creators, brands, and businesses use and love Outfame, regardless of industry or niche."
    },
    {
        question: "Is Outfame worth it?",
        answer: "Outfame stands out as the best Instagram growth service, expertly crafted to expand your audience and boost your revenue. By automating your Instagram growth and engagement, our platform is engineered to maximize efficiency, saving you valuable time, money, and effort. Experience peace of mind with our 100% money-back guarantee, should you not witness net-positive growth within the initial 30 days of your subscription. 🚀",
        hidden: true
    },
    {
        question: "Is Outfame legit for Instagram?",
        answer: "Outfame is a legitimate Instagram growth service, adhering strictly to Instagram's terms of service. Outfame's platform is designed to avoid spammy tactics and follow-for-follow schemes, focusing instead on attracting genuine Instagram users to your profile. This approach ensures your account remains safe, avoiding bans or flags, while effectively converting interested users into followers.",
        hidden: true
    },
    {
        question: "What is the refund policy?",
        answer: "We are confident in the value that Outfame provides. However, if for any reason you are not satisfied, we offer a no-questions-asked, 100% money-back guarantee if you don't see net-postive growth after the first 30 days of your subscription. We prioritize your satisfaction and growth, and our support team is always ready to assist with any concerns or issues you may have."
    }
]
export default function LandingFAQ({ className, title, subtitle, faqs, h3, hideSchema = false }: { className?: string; title?: string; subtitle?: string; faqs?: { question: string; answer: string; hidden?: boolean }[]; h3?: string; hideSchema?: boolean }) {
    if (!faqs) faqs = defaultFaqs
    return (
        <div className={classNames(className, "bg-white")}>
            <div className="mx-auto max-w-7xl px-6 py-10 sm:py-32 lg:px-8 lg:py-12">
                <div className="mx-auto max-w-2xl text-center">
                    <span className="badge-gradient-1 mb-5 inline-flex rounded-full px-4 py-2 uppercase leading-6 tracking-[0.5px] text-indigo-600">
                        <h2 className="text-gradient-1 bg-clip-text text-sm font-extrabold text-transparent" dangerouslySetInnerHTML={{ __html: title ? title : "FAQs about Outfame" }}></h2>
                    </span>
                    <h3 className="text-3xl font-bold tracking-tight text-gray-900 md:text-5xl" dangerouslySetInnerHTML={{ __html: h3 ? h3 : `Got questions?<br /> We got <span class="text-transparent text-gradient-1 bg-clip-text">the answers</span>` }}></h3>
                    <p className="mt-6 text-lg leading-7 text-gray-600 lg:text-xl lg:leading-8">{subtitle ? subtitle : "Outfame helps you get more Instagram followers, likes and comments (and a bunch of other stuff) 50x faster using GPT-o3."}</p>
                </div>
                <div className="mx-auto max-w-4xl divide-y divide-gray-900/10">
                    <dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
                        {faqs.map((faq) => (
                            <Disclosure as="div" key={faq.question} className={classNames(faq.hidden ? "hidden" : "block")}>
                                {({ open }) => (
                                    <>
                                        <dt className="rounded-xl bg-gray-100 px-6 py-5">
                                            <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
                                                <h3 className="text-lg font-semibold leading-7">{faq.question}</h3>
                                                <p className="ml-6 flex h-7 items-center">{open ? <MinusSmallIcon className="h-6 w-6" aria-hidden="true" /> : <PlusSmallIcon className="h-6 w-6" aria-hidden="true" />}</p>
                                            </Disclosure.Button>
                                        </dt>
                                        {/* unmount={false}: closed answers stay in the server HTML (hidden), so crawlers see the text the FAQ schema quotes. */}
                                        <Disclosure.Panel as="dd" unmount={false} className="mt-2 px-6 py-5 pr-12">
                                            <p className="text-lg leading-7 text-gray-600" dangerouslySetInnerHTML={{ __html: faq.answer }}></p>
                                        </Disclosure.Panel>
                                    </>
                                )}
                            </Disclosure>
                        ))}
                    </dl>
                </div>
            </div>

            {/* JSON-LD FAQ Schema */}
            {!hideSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            mainEntity: faqs.map((faq) => ({
                                "@type": "Question",
                                name: faq.question,
                                acceptedAnswer: {
                                    "@type": "Answer",
                                    text: faq.answer.replaceAll(/<[^>]*>?/gm, "").replaceAll("&nbsp;", " ")
                                }
                            }))
                        })
                    }}
                />
            )}
        </div>
    )
}
